import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import "./WorkPassportProductNavigation.css";

const WORK_PASSPORT_NAVIGATION_ITEMS = [
  {
    id: "challenge",
    label: "The challenge",
    href: "#work-passport-compliance-problem",
    targetSelector: "#work-passport-compliance-problem",
  },
  {
    id: "compliance-model",
    label: "Compliance model",
    href: "#work-passport-requirements-model",
    targetSelector: "#work-passport-requirements-model",
  },
  {
    id: "workforce-readiness",
    label: "Workforce readiness",
    href: "#work-passport-readiness-journey",
    targetSelector: "#work-passport-readiness-journey",
  },
  {
    id: "digital-passport",
    label: "Digital passport",
    href: "#work-passport-digital-passport",
    targetSelector: "#work-passport-digital-passport",
  },
  {
    id: "scheduling",
    label: "Scheduling",
    href: "#work-passport-renewal-scheduler",
    targetSelector: "#work-passport-renewal-scheduler",
  },
  {
    id: "training-delivery",
    label: "Training delivery",
    href: "#work-passport-connected-compliance-loop",
    targetSelector:
      "#work-passport-connected-compliance-loop",
  },
];

const WORK_PASSPORT_DEMONSTRATION_TARGET = {
  href: "#work-passport-final-demonstration",
  targetSelector: "#work-passport-final-demonstration",
};

const WORK_PASSPORT_DEFAULT_NAVIGATION_ID =
  WORK_PASSPORT_NAVIGATION_ITEMS[0].id;

const WORK_PASSPORT_NAVIGATION_GAP = 12;
const WORK_PASSPORT_MANUAL_SELECTION_DURATION = 1400;

function workPassportPrefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches
  );
}

function isModifiedWorkPassportNavigationEvent(event) {
  return (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  );
}

export default function WorkPassportProductNavigation({
  demonstrationHref =
    WORK_PASSPORT_DEMONSTRATION_TARGET.href,
}) {
  const navigationRef = useRef(null);
  const scrollerRef = useRef(null);
  const navigationLinkRefs = useRef({});
  const manualNavigationRef = useRef(null);

  const [activeNavigationId, setActiveNavigationId] =
    useState(WORK_PASSPORT_DEFAULT_NAVIGATION_ID);

  /*
   * Calculate an offset only when another header or navigation
   * component is genuinely fixed or sticky on screen.
   *
   * On the current Work Passport page the primary Navbar remains in
   * normal document flow, so this resolves to 0px and removes the gap.
   */
  useEffect(() => {
    const navigationElement = navigationRef.current;

    if (!navigationElement) {
      return undefined;
    }

    let animationFrameId = null;

    const getExternalPinnedElements = () =>
      Array.from(
        document.querySelectorAll("header, nav")
      ).filter((element) => {
        if (
          element === navigationElement ||
          navigationElement.contains(element)
        ) {
          return false;
        }

        const styles = window.getComputedStyle(element);
        const rect = element.getBoundingClientRect();

        const isPinned =
          styles.position === "fixed" ||
          styles.position === "sticky";

        const isVisible =
          styles.display !== "none" &&
          styles.visibility !== "hidden" &&
          rect.height > 0 &&
          rect.bottom > 0 &&
          rect.top <= 8;

        return isPinned && isVisible;
      });

    const updateStickyPosition = () => {
      animationFrameId = null;

      const pinnedNavigationBottom =
        getExternalPinnedElements().reduce(
          (currentBottom, element) => {
            const rect = element.getBoundingClientRect();

            return Math.max(currentBottom, rect.bottom);
          },
          0
        );

      navigationElement.style.setProperty(
        "--work-passport-product-navigation-sticky-top",
        `${Math.max(
          0,
          Math.round(pinnedNavigationBottom)
        )}px`
      );
    };

    const requestStickyPositionUpdate = () => {
      if (animationFrameId !== null) {
        return;
      }

      animationFrameId = window.requestAnimationFrame(
        updateStickyPosition
      );
    };

    updateStickyPosition();

    window.addEventListener(
      "scroll",
      requestStickyPositionUpdate,
      { passive: true }
    );

    window.addEventListener(
      "resize",
      requestStickyPositionUpdate
    );

    let resizeObserver;

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(
        requestStickyPositionUpdate
      );

      getExternalPinnedElements().forEach((element) => {
        resizeObserver.observe(element);
      });
    }

    return () => {
      window.removeEventListener(
        "scroll",
        requestStickyPositionUpdate
      );

      window.removeEventListener(
        "resize",
        requestStickyPositionUpdate
      );

      resizeObserver?.disconnect();

      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  const getNavigationOffset = useCallback(() => {
    const navigationElement = navigationRef.current;

    if (!navigationElement) {
      return 0;
    }

    const styles =
      window.getComputedStyle(navigationElement);

    const navigationTop = Number.parseFloat(styles.top);

    const topOffset = Number.isFinite(navigationTop)
      ? Math.max(0, navigationTop)
      : 0;

    return (
      topOffset +
      navigationElement.offsetHeight +
      WORK_PASSPORT_NAVIGATION_GAP
    );
  }, []);

  /*
   * Keep the active link matched to the current high-level section.
   *
   * Command Centre remains part of Workforce Readiness.
   * Role Control and Implementation remain part of Training Delivery.
   */
  useEffect(() => {
    let animationFrameId = null;

    const updateActiveNavigation = () => {
      animationFrameId = null;

      const navigationOffset = getNavigationOffset();

      const activationPosition =
        window.scrollY + navigationOffset + 4;

      const availableSections =
        WORK_PASSPORT_NAVIGATION_ITEMS.map(
          (navigationItem) => {
            const sectionElement = document.querySelector(
              navigationItem.targetSelector
            );

            if (!sectionElement) {
              return null;
            }

            return {
              ...navigationItem,
              element: sectionElement,
              top:
                sectionElement.getBoundingClientRect().top +
                window.scrollY,
            };
          }
        ).filter(Boolean);

      if (availableSections.length === 0) {
        return;
      }

      const manualNavigation =
        manualNavigationRef.current;

      if (
        manualNavigation &&
        performance.now() < manualNavigation.expiresAt
      ) {
        const selectedSection = availableSections.find(
          (section) =>
            section.id === manualNavigation.id
        );

        if (selectedSection) {
          const sectionViewportTop =
            selectedSection.element.getBoundingClientRect().top;

          const hasReachedSection =
            Math.abs(
              sectionViewportTop - navigationOffset
            ) <= 24;

          if (!hasReachedSection) {
            setActiveNavigationId((currentId) =>
              currentId === manualNavigation.id
                ? currentId
                : manualNavigation.id
            );

            return;
          }
        }

        manualNavigationRef.current = null;
      } else if (manualNavigation) {
        manualNavigationRef.current = null;
      }

      let nextActiveNavigationId =
        availableSections[0].id;

      availableSections.forEach((section) => {
        if (section.top <= activationPosition) {
          nextActiveNavigationId = section.id;
        }
      });

      setActiveNavigationId((currentId) =>
        currentId === nextActiveNavigationId
          ? currentId
          : nextActiveNavigationId
      );
    };

    const requestNavigationUpdate = () => {
      if (animationFrameId !== null) {
        return;
      }

      animationFrameId = window.requestAnimationFrame(
        updateActiveNavigation
      );
    };

    updateActiveNavigation();

    window.addEventListener(
      "scroll",
      requestNavigationUpdate,
      { passive: true }
    );

    window.addEventListener(
      "resize",
      requestNavigationUpdate
    );

    window.addEventListener(
      "hashchange",
      requestNavigationUpdate
    );

    return () => {
      window.removeEventListener(
        "scroll",
        requestNavigationUpdate
      );

      window.removeEventListener(
        "resize",
        requestNavigationUpdate
      );

      window.removeEventListener(
        "hashchange",
        requestNavigationUpdate
      );

      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [getNavigationOffset]);

  /*
   * Keep the active link visible in the horizontal navigation rail.
   */
  useEffect(() => {
    const scroller = scrollerRef.current;

    const activeLink =
      navigationLinkRefs.current[activeNavigationId];

    if (
      !scroller ||
      !activeLink ||
      scroller.scrollWidth <= scroller.clientWidth
    ) {
      return;
    }

    const visibleStart = scroller.scrollLeft + 12;

    const visibleEnd =
      scroller.scrollLeft + scroller.clientWidth - 12;

    const linkStart = activeLink.offsetLeft;

    const linkEnd =
      activeLink.offsetLeft + activeLink.offsetWidth;

    let nextScrollLeft = scroller.scrollLeft;

    if (linkStart < visibleStart) {
      nextScrollLeft = linkStart - 18;
    } else if (linkEnd > visibleEnd) {
      nextScrollLeft =
        linkEnd - scroller.clientWidth + 18;
    }

    const maximumScrollLeft = Math.max(
      0,
      scroller.scrollWidth - scroller.clientWidth
    );

    nextScrollLeft = Math.min(
      maximumScrollLeft,
      Math.max(0, nextScrollLeft)
    );

    if (
      Math.abs(nextScrollLeft - scroller.scrollLeft) > 1
    ) {
      scroller.scrollTo({
        left: nextScrollLeft,
        behavior: workPassportPrefersReducedMotion()
          ? "auto"
          : "smooth",
      });
    }
  }, [activeNavigationId]);

  const scrollToTarget = (
    targetElement,
    destinationHref
  ) => {
    const navigationOffset = getNavigationOffset();

    const targetPosition =
      targetElement.getBoundingClientRect().top +
      window.scrollY -
      navigationOffset;

    window.scrollTo({
      top: Math.max(0, Math.round(targetPosition)),
      behavior: workPassportPrefersReducedMotion()
        ? "auto"
        : "smooth",
    });

    if (window.location.hash !== destinationHref) {
      window.history.replaceState(
        null,
        "",
        destinationHref
      );
    }
  };

  const handleNavigation = (
    event,
    navigationItem
  ) => {
    if (
      isModifiedWorkPassportNavigationEvent(event)
    ) {
      return;
    }

    const sectionElement = document.querySelector(
      navigationItem.targetSelector
    );

    if (!sectionElement) {
      return;
    }

    event.preventDefault();

    manualNavigationRef.current = {
      id: navigationItem.id,
      expiresAt:
        performance.now() +
        WORK_PASSPORT_MANUAL_SELECTION_DURATION,
    };

    setActiveNavigationId(navigationItem.id);

    scrollToTarget(
      sectionElement,
      navigationItem.href
    );
  };

  const handleDemonstrationNavigation = (event) => {
    if (
      isModifiedWorkPassportNavigationEvent(event) ||
      !demonstrationHref.startsWith("#")
    ) {
      return;
    }

    const demonstrationElement =
      document.querySelector(demonstrationHref);

    if (!demonstrationElement) {
      return;
    }

    event.preventDefault();
    manualNavigationRef.current = null;

    scrollToTarget(
      demonstrationElement,
      demonstrationHref
    );
  };

  return (
    <nav
      ref={navigationRef}
      id="work-passport-product-navigation"
      className="work-passport-product-navigation"
      aria-label="Work Passport page navigation"
    >
      <div className="work-passport-product-navigation__inner">
        <div className="work-passport-product-navigation__identity">
          <strong>Work Passport</strong>

          <span>
            <i aria-hidden="true" />
            Page navigation
          </span>
        </div>

        <div
          ref={scrollerRef}
          className="work-passport-product-navigation__scroller"
        >
          {WORK_PASSPORT_NAVIGATION_ITEMS.map(
            (navigationItem) => {
              const isActive =
                activeNavigationId === navigationItem.id;

              return (
                <a
                  key={navigationItem.id}
                  ref={(linkElement) => {
                    if (linkElement) {
                      navigationLinkRefs.current[
                        navigationItem.id
                      ] = linkElement;
                    } else {
                      delete navigationLinkRefs.current[
                        navigationItem.id
                      ];
                    }
                  }}
                  className={[
                    "work-passport-product-navigation__link",
                    isActive
                      ? "work-passport-product-navigation__link--active"
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  href={navigationItem.href}
                  aria-current={
                    isActive ? "location" : undefined
                  }
                  onClick={(event) =>
                    handleNavigation(
                      event,
                      navigationItem
                    )
                  }
                >
                  <i aria-hidden="true" />

                  <span>
                    {navigationItem.label}
                  </span>
                </a>
              );
            }
          )}
        </div>

        <a
          className="work-passport-product-navigation__demonstration"
          href={demonstrationHref}
          onClick={handleDemonstrationNavigation}
        >
          <span>Book a demo</span>
          <span aria-hidden="true">→</span>
        </a>
      </div>
    </nav>
  );
}